import { z } from 'zod';
import { EnvironmentSchema } from '../domain/application.js';
import { EngagementStageSchema } from '../domain/engagement.js';
import { IdSchema, IsoDateTimeSchema, listResponseSchema, OrgIdSchema, PaginationQuerySchema, UserIdSchema, VersionSchema, } from './common.js';
export const ENGAGEMENT_NAME_MAX = 160;
const NameSchema = z.string().trim().min(1).max(ENGAGEMENT_NAME_MAX);
/**
 * A test campaign against one application in one environment.
 *
 * `stage` is reported but never client-settable: transitions run through the whitelist in
 * `domain/engagement.ts` plus the backend's approval-record check (§5), so the only ways
 * the stage moves are the scope, approval and agent-run routes.
 */
export const EngagementSchema = z.object({
    id: IdSchema,
    orgId: OrgIdSchema,
    applicationId: IdSchema,
    name: NameSchema,
    environment: EnvironmentSchema,
    stage: EngagementStageSchema,
    /** Engagement lead — the human accountable for approvals on this engagement. */
    ownerUserId: UserIdSchema,
    /** Highest scope version recorded so far; absent until the scope route has been called. */
    currentScopeVersion: z.number().int().positive().optional(),
    createdAt: IsoDateTimeSchema,
    createdBy: UserIdSchema,
    updatedAt: IsoDateTimeSchema,
    version: VersionSchema,
});
/**
 * POST /v1/engagements
 *
 * Creates the engagement in `DRAFT`. Neither `stage` nor `orgId` is accepted from the body:
 * the stage is fixed by the server and the tenant comes from the caller's JWT.
 */
export const CreateEngagementRequestSchema = z.strictObject({
    applicationId: IdSchema,
    name: NameSchema,
    environment: EnvironmentSchema,
    /** Defaults to the caller when omitted. */
    ownerUserId: UserIdSchema.optional(),
});
/**
 * GET /v1/engagements
 *
 * `applicationId` is served by GSI1 (`APP#{appId}` / `ENG#{createdAt}`). `stage` is a
 * post-query filter, so a narrow `stage` on a large tenant may need several pages.
 */
export const ListEngagementsQuerySchema = PaginationQuerySchema.extend({
    applicationId: IdSchema.optional(),
    stage: EngagementStageSchema.optional(),
});
export const ListEngagementsResponseSchema = listResponseSchema(EngagementSchema);
const ENGAGEMENT_PATCH_FIELDS = ['name', 'ownerUserId'];
/**
 * PATCH /v1/engagements/{id}
 *
 * Only the two metadata fields are mutable. `applicationId` and `environment` are fixed for
 * the life of an engagement because an existing approved scope was granted against them, and
 * `stage` is excluded for the reason given on `EngagementSchema`.
 */
export const PatchEngagementRequestSchema = z
    .strictObject({
    expectedVersion: VersionSchema,
    name: NameSchema.optional(),
    ownerUserId: UserIdSchema.optional(),
})
    .refine((body) => ENGAGEMENT_PATCH_FIELDS.some((field) => body[field] !== undefined), {
    message: 'At least one field to update is required',
});
/**
 * POST /v1/engagements/{id}/transitions
 *
 * Asks to move an engagement on — into a gate, or out of a stage whose work is done. `from` is
 * the stage the requester was looking at, and the move is refused if the engagement has moved
 * since: "move to triage" means something different when said about an engagement someone else
 * has already sent for a report.
 *
 * Kept separate from PATCH on purpose. Changing a name and advancing an engagement through its
 * lifecycle are different privileges with different rules, and a stage change hidden among
 * metadata edits is the kind of change an audit trail exists to make visible.
 */
export const TransitionEngagementRequestSchema = z.strictObject({
    from: EngagementStageSchema,
    to: EngagementStageSchema,
});
//# sourceMappingURL=engagement.js.map